import {
  AloftPrice,
  AloftPriceJSON,
  getRegressionModelWarningForFeature,
  getSensitivityModelErrorForFeature,
  isRegressionModelReliableForFeature,
  isSensitivityModelReliableForFeature,
  orUndefined,
} from '@aloft/toolkit-shared';
import { Flex, Icon, Tooltip, Typography } from '@aloft/toolkit-sui';

import { FieldPath, useWatch } from 'react-hook-form';

import { selectUnitRates } from '../../../redux/features/selectors/unitRatesSelectors/selectUnitRates.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { PropertyGridForm } from '../types.ts';
import { readOnlyFieldProps, readOnlyFieldTypographyProps } from './styles.ts';
import { PropertyGridFieldProps } from './types.ts';

export const ReadOnlyUnitRateField = ({
  columnIndex,
  field,
  colSpan = 1,
  control,
}: PropertyGridFieldProps) => {
  const { manualAdjustmentKey, unit, unitRateKey } = field;

  const unitRateFieldName =
    `columns.${columnIndex}.propertyData.editableData.salesComparison.unitRates.${unitRateKey}` as FieldPath<PropertyGridForm>;

  const storedUnitRate = useWatch({ control, name: unitRateFieldName }) as
    | AloftPriceJSON
    | undefined;

  const columnType = useWatch({ control, name: `columns.${columnIndex}.type` });

  const unitRateModels = useAppSelector(selectUnitRates);

  const showWarning =
    unitRateKey != null &&
    ((columnType === 'regression' &&
      !isRegressionModelReliableForFeature(unitRateModels, unitRateKey)) ||
      (columnType === 'sensitivity' &&
        !isSensitivityModelReliableForFeature(unitRateModels, unitRateKey)));

  const warningMessage =
    (unitRateKey != null &&
      ((columnType === 'regression' &&
        getRegressionModelWarningForFeature(unitRateModels.regression, unitRateKey)) ||
        (columnType === 'sensitivity' &&
          getSensitivityModelErrorForFeature(unitRateModels, unitRateKey)))) ||
    '';

  if (!unitRateKey) return <Typography {...readOnlyFieldProps(colSpan)}>-</Typography>;

  const displayRate = orUndefined((value: AloftPriceJSON) => AloftPrice.schema.parse(value))(
    storedUnitRate,
  );
  const forceNegative = manualAdjustmentKey === 'actualAge' && unitRateKey === 'actualAge';
  const normalizedRate =
    forceNegative && displayRate
      ? AloftPrice.fromUnitAmount(-Math.abs(displayRate.unitAmount), displayRate.currency)
      : displayRate;
  const rateText = normalizedRate?.toString(true);

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      __dangerDoNotUseSx={{
        ...readOnlyFieldProps(colSpan).__dangerDoNotUseSx,
        paddingRight: '6px',
      }}
    >
      <Typography {...readOnlyFieldTypographyProps} __dangerDoNotUseSx={undefined}>
        {rateText ?? '-'}
      </Typography>
      <Flex alignItems="center" gap="0.25rem" flexShrink={0}>
        {unit && displayRate && (
          <Typography
            variant="body2"
            inline
            color="textSecondary"
            __dangerDoNotUseSx={{
              flexShrink: 0,
              lineHeight: 1,
            }}
          >
            /{unit}
          </Typography>
        )}
        {showWarning && (
          <Tooltip label={warningMessage} placement="top">
            <Flex alignItems="center">
              <Icon icon="error-warning" size="md" color="textWarning" />
            </Flex>
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
};
